"use client";

import { FC, ReactNode, ReactElement } from "react";
import {
  chakra,
  // eslint-disable-next-line no-restricted-imports
  Dialog as ChakraDialog,
  DialogRootProps,
  Portal,
  CloseButton,
} from "@chakra-ui/react";
import { CircleXIcon } from "lucide-react";
import {
  simpleDialogContentRecipe,
  simpleDialogBodyRecipe,
  simpleDialogPositionerRecipe,
  simpleDialogBackdropRecipe,
  simpleDialogHeaderRecipe,
  simpleDialogCloseTriggerRecipe,
} from "./dialog.recipe";

type Props = DialogRootProps & {
  header?: ReactNode;
  trigger?: ReactElement;
  initialFocusEl?: () => HTMLElement | null;
  onClose: VoidFunction;
};

export const DialogRoot = ChakraDialog.Root;
export const DialogTrigger = ChakraDialog.Trigger;
export const DialogCloseTrigger = chakra(
  ChakraDialog.CloseTrigger,
  simpleDialogCloseTriggerRecipe,
);
export const DialogBackdrop = chakra(
  ChakraDialog.Backdrop,
  simpleDialogBackdropRecipe,
);
export const DialogHeader = chakra(
  ChakraDialog.Header,
  simpleDialogHeaderRecipe,
);
export const DialogBody = chakra(ChakraDialog.Body, simpleDialogBodyRecipe);
export const DialogContent = chakra(
  ChakraDialog.Content,
  simpleDialogContentRecipe,
);
export const DialogPositioner = chakra(
  ChakraDialog.Positioner,
  simpleDialogPositionerRecipe,
);

const SimpleDialog: FC<Props> = ({
  trigger,
  header,
  children,
  initialFocusEl,
  onClose,
  ...props
}) => (
  <DialogRoot
    scrollBehavior="inside"
    closeOnInteractOutside
    closeOnEscape
    size="cover"
    initialFocusEl={initialFocusEl}
    onOpenChange={(details: { open: boolean }) => {
      if (!details.open) {
        onClose();
      }
    }}
    {...props}
  >
    {trigger ? (
      <DialogTrigger asChild>{trigger}</DialogTrigger>
    ) : (
      <DialogTrigger />
    )}

    <Portal>
      <DialogBackdrop />

      <DialogPositioner>
        <DialogContent>
          <DialogHeader>
            {header}

            <DialogCloseTrigger asChild>
              <button onClick={onClose}>
                <CircleXIcon color="white" />
              </button>
            </DialogCloseTrigger>
          </DialogHeader>

          <DialogBody>{children}</DialogBody>
        </DialogContent>
      </DialogPositioner>
    </Portal>
  </DialogRoot>
);

export default SimpleDialog;
